/**
 * Chat Cart Orders – shared types (see docs/CHAT_CART_ORDERS_FRONTEND_IMPLEMENTATION.md).
 */

export type ChatCartOrderStatus =
  | 'shared'
  | 'priced'
  | 'changes_requested'
  | 'accepted'
  | 'preparing'
  | 'ready'
  | 'out_for_delivery'
  | 'delivered'
  | 'cancelled';

export const CHAT_CART_ORDER_STATUS_LABELS: Record<ChatCartOrderStatus, string> = {
  shared: 'Shared',
  priced: 'Priced',
  changes_requested: 'Changes requested',
  accepted: 'Accepted',
  preparing: 'Preparing',
  ready: 'Ready',
  out_for_delivery: 'On the Way',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

export interface ChatCartOrderItem {
  id: string;
  cartOrderId: string;
  /** Nullable for manual (free-text) items that are not from the catalog. */
  stockItemId: string | null;
  quantity: number;
  /** Nullable for manual items (organizer prices them later). */
  unitPrice: number | null;
  itemName: string;
  itemImage?: string;
  /** Free-text quantity for manual items, e.g. "2 kg", "3 packs". */
  quantityText?: string | null;
  /** Optional per-item note for manual items. */
  note?: string | null;
  /** Organizer availability flag (review). False = out of stock; excluded from total. */
  isAvailable?: boolean;
  /** Organizer per-item comment set during review. */
  organizerComment?: string | null;
  sortOrder: number;
  createdAt: string;
}

export interface ChatCartOrder {
  id: string;
  branchId: string;
  customerId: string;
  requestId: string;
  status: ChatCartOrderStatus;
  deliverBy?: string | null;
  organizerNotes?: string | null;
  messageId?: string | null;
  createdAt: string;
  updatedAt: string;
  items?: ChatCartOrderItem[];
  branch?: { id: string; name?: string; [k: string]: any };
  customer?: { id: string; displayName?: string; phone?: string; [k: string]: any };
  request?: { id: string; status: string; [k: string]: any };
}

/**
 * A single line in a create-cart-order request. Two mixable shapes:
 * - Catalog item: `{ stockItemId, quantity }`
 * - Manual (free-text) item: `{ itemName, quantityText?, note? }`
 */
export interface CreateChatCartOrderItem {
  stockItemId?: string;
  quantity?: number;
  itemName?: string;
  quantityText?: string;
  note?: string;
}

export interface CreateChatCartOrderRequest {
  items: CreateChatCartOrderItem[];
  notes?: string;
}

/**
 * Compact cart-order summary embedded in a chat message DTO (`message.cartOrder`).
 * Rendered as an order card inside the chat box.
 */
export interface ChatCartOrderSummary {
  id: string;
  status: ChatCartOrderStatus;
  deliverBy?: string | null;
  organizerNotes?: string | null;
  itemCount?: number;
  estimatedTotal?: number | null;
  /** Organizer-confirmed grand total (set during review). */
  total?: number | null;
  /** Set once the customer accepts; non-null means the order is frozen (read-only). */
  acceptedAt?: string | null;
  items?: ChatCartOrderItem[];
}

export interface UpdateChatCartOrderRequest {
  status?: ChatCartOrderStatus;
  deliverBy?: string;
  organizerNotes?: string;
}
